// PE-6102 / PE-6105 — BookAppointment module visibility and self-scheduling mappers
import { DecryptedUserPermissions } from '../models/user-permissions.model';

export const POST_LOGIN_REDIRECT_INTENT_KEY = 'post_login_redirect_intent';
export const BOOK_APPOINTMENT_REDIRECT_INTENT = 'book-appointment';
export const APPOINTMENTS_REFRESH_PENDING_KEY = 'appointments_refresh_pending';

export interface TenantSettingRow {
  settingname?: string;
  settingvalue?: string;
  jsondata?: string;
  ishtml?: boolean;
}

export interface AppointmentTypeEligibility {
  newPatient: string[];
  returningPatient: string[];
}

export interface AppointmentTypeOption {
  appointmenttypedescription: string;
  appointmentType: Record<string, unknown>;
}

export interface LocationOption {
  locationdescription: string;
  location: Record<string, unknown>;
}

export interface ProviderOption {
  providerName: string;
  provider: Record<string, unknown>;
}

export const SLOT_STATUS_OPEN = 'Open';

export interface BookAppointmentSlot {
  nTemplateAllocationMasterID: string;
  nTemplateAllocationID: string;
  displayLabel: string;
  dtStartTime?: string;
  dtEndTime?: string;
  dtStartDate?: string;
  formattedDate?: string;
  formattedTime?: string;
  providerName?: string;
  locationName?: string;
  providerId?: string;
  locationId?: string;
  slotDate?: string;
  nLineNumber?: string;
  // PE-6106 — mirrors Self-Scheduling-App StatusNew (Open | Booked)
  slotStatus?: string;
}

export interface GroupedDateSlots {
  date: string;
  dtStartDate?: string;
  slots: BookAppointmentSlot[];
}

export interface GroupedProviderSlots {
  name: string;
  id: string;
  availability: GroupedDateSlots[];
}

export interface GroupedLocationSlots {
  location: string;
  locationId: string;
  providers: GroupedProviderSlots[];
}

// PE-6103 — apply booking intent after login/MFA or representative patient selection
export function resolvePostLoginRedirectRoute(
  defaultRoute: string,
  userPermissions?: unknown
): string {
  if (defaultRoute !== '/home') {
    return defaultRoute;
  }

  const intent = sessionStorage.getItem(POST_LOGIN_REDIRECT_INTENT_KEY);
  if (intent !== BOOK_APPOINTMENT_REDIRECT_INTENT) {
    return defaultRoute;
  }

  sessionStorage.removeItem(POST_LOGIN_REDIRECT_INTENT_KEY);

  if (!isBookAppointmentEnabled(userPermissions)) {
    return defaultRoute;
  }

  return '/book-appointment';
}

// PE-6102 — modulesetting BookAppointment row (user_permissions.bookappointment)
export function isBookAppointmentEnabled(userPermissions: unknown): boolean {
  if (!userPermissions || typeof userPermissions !== 'object') {
    return false;
  }
  return (userPermissions as DecryptedUserPermissions).bookappointment === true;
}

export function asRecordArray(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data as Record<string, unknown>[];
  }
  const wrapped = data as { data?: unknown };
  if (Array.isArray(wrapped?.data)) {
    return wrapped.data as Record<string, unknown>[];
  }
  // PE2 / BFF may double-wrap: { data: { data: [...] } }
  if (wrapped?.data && typeof wrapped.data === 'object') {
    const inner = wrapped.data as { data?: unknown };
    if (Array.isArray(inner?.data)) {
      return inner.data as Record<string, unknown>[];
    }
  }
  return [];
}

export function asSettingsArray(data: unknown): TenantSettingRow[] {
  return asRecordArray(data) as TenantSettingRow[];
}

export function findSettingValue(settings: unknown, settingName: string): string {
  const rows = asSettingsArray(settings);
  const match = rows.find(
    (row) => String(row?.settingname ?? '').toLowerCase() === settingName.toLowerCase()
  );
  return String(match?.settingvalue ?? match?.jsondata ?? '').trim();
}

// PE-6177 — setting key from PE-6176 (Admin OwnProviderAppointmentTypes CSV of appointment type IDs)
export const OWN_PROVIDER_APPOINTMENT_TYPES_SETTING = 'OwnProviderAppointmentTypes';

// PE-6177 — parse configured appointment type IDs (empty / "[]" = no lock)
export function parseOwnProviderAppointmentTypeIds(settingValue: string): string[] {
  if (!settingValue?.trim() || settingValue.trim() === '[]') {
    return [];
  }
  return settingValue
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0 && id !== '[]');
}

// PE-6177 — true when selected appointment type is in OwnProviderAppointmentTypes
export function isAppointmentTypeConfiguredForOwnProvider(
  selectedAppointmentTypeId: string | null | undefined,
  configuredIds: string[]
): boolean {
  if (!configuredIds.length) {
    return false;
  }
  const fullId = String(selectedAppointmentTypeId ?? '').trim();
  if (!fullId) {
    return false;
  }
  const trimmedId = trimAppointmentTypeIdForApi(fullId);
  return configuredIds.some((configuredId) => {
    const cfg = String(configuredId ?? '').trim();
    if (!cfg) {
      return false;
    }
    return cfg === fullId || trimAppointmentTypeIdForApi(cfg) === trimmedId;
  });
}

// PE-6177 — match patient's own provider in the scheduling provider list
export function findProviderOptionById(
  providers: ProviderOption[],
  providerId: string | null | undefined
): ProviderOption | null {
  const target = trimProviderIdForApi(providerId);
  if (!target || !providers?.length) {
    return null;
  }
  return (
    providers.find(
      (entry) => trimProviderIdForApi(entry.provider?.['providerid']) === target
    ) ?? null
  );
}

// PE-6231 — reschedule prefill: match provider by id, then by display name (ignore Dr. prefix)
export function findProviderOptionByIdOrName(
  providers: ProviderOption[],
  providerId?: string | null,
  providerName?: string | null
): ProviderOption | null {
  const byId = findProviderOptionById(providers, providerId);
  if (byId) {
    return byId;
  }
  const targetName = normalizeProviderDisplayName(providerName);
  if (!targetName || !providers?.length) {
    return null;
  }
  return (
    providers.find((entry) => normalizeProviderDisplayName(entry.providerName) === targetName) ??
    null
  );
}

// PE-6231 — reschedule prefill: match appointment type by id, then by description
export function findAppointmentTypeOption(
  types: AppointmentTypeOption[],
  appointmentTypeId?: string | null,
  appointmentTypeName?: string | null
): AppointmentTypeOption | null {
  const targetId = trimAppointmentTypeIdForApi(appointmentTypeId);
  if (targetId && types?.length) {
    const byId = types.find(
      (entry) =>
        trimAppointmentTypeIdForApi(entry.appointmentType?.['appointmenttypeid']) === targetId
    );
    if (byId) {
      return byId;
    }
  }
  const targetName = normalizeCompareValue(appointmentTypeName);
  if (!targetName || !types?.length) {
    return null;
  }
  return (
    types.find((entry) => normalizeCompareValue(entry.appointmenttypedescription) === targetName) ??
    null
  );
}

// PE-6231 — reschedule prefill: match location by locationid/emrid, then by name
export function findLocationOption(
  locations: LocationOption[],
  locationId?: string | null,
  locationName?: string | null
): LocationOption | null {
  const targetId = String(locationId ?? '').trim();
  const targetPrefix = trimAppointmentTypeIdForApi(targetId);
  if (targetPrefix && locations?.length) {
    const byId = locations.find((entry) => {
      const apiId = String(entry.location?.['locationid'] ?? '').trim();
      const emrId = resolveLocationEmrId(entry);
      return (
        idsMatchForPrefill(apiId, targetId, targetPrefix) ||
        idsMatchForPrefill(emrId, targetId, targetPrefix)
      );
    });
    if (byId) {
      return byId;
    }
  }
  const targetName = normalizeCompareValue(locationName);
  if (!targetName || !locations?.length) {
    return null;
  }
  return (
    locations.find((entry) => normalizeCompareValue(entry.locationdescription) === targetName) ??
    null
  );
}

function idsMatchForPrefill(candidate: string, fullTarget: string, targetPrefix: string): boolean {
  const value = String(candidate ?? '').trim();
  if (!value) {
    return false;
  }
  return (
    value === fullTarget ||
    trimAppointmentTypeIdForApi(value) === targetPrefix ||
    value === targetPrefix
  );
}

function normalizeProviderDisplayName(name: unknown): string {
  return String(name ?? '')
    .replace(/^dr\.?\s+/i, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

// PE-6105 — parse AppointmentType settingvalue into new/returning patient id lists
export function parseAppointmentTypeSettingValue(settingValue: string): AppointmentTypeEligibility {
  const defaultValue: AppointmentTypeEligibility = { newPatient: [], returningPatient: [] };
  if (!settingValue) {
    return defaultValue;
  }
  try {
    const parsed = JSON.parse(settingValue) as {
      newPatient?: unknown;
      returningPatient?: unknown;
    };
    return {
      newPatient: Array.isArray(parsed?.newPatient) ? parsed.newPatient.map(String) : [],
      returningPatient: Array.isArray(parsed?.returningPatient)
        ? parsed.returningPatient.map(String)
        : [],
    };
  } catch {
    return defaultValue;
  }
}

export function parseAppointmentSlotsDuration(settingValue: string): { min: number; max: number } {
  if (!settingValue) {
    return { min: 0, max: 0 };
  }
  try {
    const parsed = JSON.parse(settingValue) as { min?: string; max?: string };
    return {
      min: parsed?.min ? parseInt(String(parsed.min).replace(/\D/g, ''), 10) : 0,
      max: parsed?.max ? parseInt(String(parsed.max).replace(/\D/g, ''), 10) : 0,
    };
  } catch {
    return { min: 0, max: 0 };
  }
}

export function resolveTemplateIdFromSettings(settings: unknown): string {
  return findSettingValue(settings, 'QSuiteTemplate') || findSettingValue(settings, 'qsuitetemplate');
}

export function mapLocationOptions(locations: unknown): LocationOption[] {
  return asRecordArray(locations)
    .map((location) => ({
      locationdescription: String(location['locationdescription'] ?? '').trim(),
      location,
    }))
    .filter((entry) => entry.locationdescription.length > 0)
    .sort((a, b) => a.locationdescription.localeCompare(b.locationdescription));
}

export function mapProviderOptions(providers: unknown): ProviderOption[] {
  return asRecordArray(providers)
    .map((provider) => {
      const firstName = String(provider['providerfirstname'] ?? '').trim();
      const lastName = String(provider['providerlastname'] ?? '').trim();
      const providerName = `${firstName} ${lastName}`.trim();
      return { providerName, provider };
    })
    .filter((entry) => entry.providerName.length > 0)
    .sort((a, b) => a.providerName.localeCompare(b.providerName));
}

export function mapAppointmentTypeOptions(types: unknown): AppointmentTypeOption[] {
  return asRecordArray(types)
    .map((appointmentType) => ({
      appointmenttypedescription: String(appointmentType['appointmenttypedescription'] ?? '').trim(),
      appointmentType,
    }))
    .filter((entry) => entry.appointmenttypedescription.length > 0)
    .sort((a, b) => a.appointmenttypedescription.localeCompare(b.appointmenttypedescription));
}

// PE-6102 — QPrime new-appointment applyAppointmentTypeDerivedFields (colourcode from appointment-types API)
export function resolveAppointmentTypeColorCode(
  appointmentType: Record<string, unknown> | null | undefined
): string {
  if (!appointmentType) {
    return '';
  }
  const raw = appointmentType['colourcode'] ?? appointmentType['colorcode'];
  if (raw === null || raw === undefined) {
    return '';
  }
  return String(raw).trim();
}

// PE-6105 — portal book flow is returning-patient only
export function filterReturningPatientAppointmentTypes(
  types: AppointmentTypeOption[],
  eligibility: AppointmentTypeEligibility
): AppointmentTypeOption[] {
  const returningIds = eligibility.returningPatient;
  if (!returningIds.length) {
    return types;
  }
  return types.filter((item) =>
    returningIds.includes(String(item.appointmentType['appointmenttypeid'] ?? ''))
  );
}

// PE-6156 — guest sign-up booking shows new-patient appointment types only
export function filterNewPatientAppointmentTypes(
  types: AppointmentTypeOption[],
  eligibility: AppointmentTypeEligibility
): AppointmentTypeOption[] {
  const newPatientIds = eligibility.newPatient;
  if (!newPatientIds.length) {
    return types;
  }
  return types.filter((item) =>
    newPatientIds.includes(String(item.appointmentType['appointmenttypeid'] ?? ''))
  );
}

export function resolveLocationEmrId(selected: LocationOption | null | undefined): string {
  if (!selected?.location) {
    return '';
  }
  const externalRef = selected.location['externalref'] as Record<string, unknown> | undefined;
  return String(externalRef?.['emrid'] ?? '').trim();
}

export function trimProviderIdForApi(rawProviderId: unknown): string {
  return trimCompositeIdForApi(rawProviderId);
}

// PE-6105 — calendar/slots APIs expect numeric appointmenttypeid prefix only
export function trimAppointmentTypeIdForApi(rawAppointmentTypeId: unknown): string {
  return trimCompositeIdForApi(rawAppointmentTypeId);
}

function trimCompositeIdForApi(rawId: unknown): string {
  const value = String(rawId ?? '').trim();
  if (!value) {
    return '';
  }
  return value.split('-')[0] ?? value;
}

export function resolveProviderIdFromOption(selected: ProviderOption | null | undefined): string {
  return trimProviderIdForApi(selected?.provider?.['providerid']);
}

// PE-6237 — calendar-dates: selected id, else CSV of configured location emrids (first load)
export function resolveCalendarLocationIdParam(
  selectedLocationId: string,
  allLocations: LocationOption[]
): string {
  const selected = String(selectedLocationId ?? '').trim();
  if (selected && selected !== '0') {
    return selected;
  }
  const ids = (allLocations ?? [])
    .map((entry) => resolveLocationEmrId(entry))
    .filter((id) => id.length > 0 && id !== '0');
  const unique = [...new Set(ids)];
  return unique.length > 0 ? unique.join(',') : '0';
}

// PE-6237 — calendar-dates: selected id, else CSV of configured provider base ids (first load)
export function resolveCalendarProviderIdParam(
  selectedProviderId: string,
  allProviders: ProviderOption[]
): string {
  const selected = String(selectedProviderId ?? '').trim();
  if (selected && selected !== '0') {
    return selected;
  }
  const ids = (allProviders ?? [])
    .map((entry) => resolveProviderIdFromOption(entry))
    .filter((id) => id.length > 0 && id !== '0');
  const unique = [...new Set(ids)];
  return unique.length > 0 ? unique.join(',') : '0';
}

// PE-6105 — book payload IDs: prefer filter selection, else fall back to chosen slot (Self-Scheduling-App pattern)
export function resolveBookProviderId(
  selectedProviderId: string,
  slot: BookAppointmentSlot | null | undefined
): string {
  const fromFilter = String(selectedProviderId ?? '').trim();
  if (fromFilter) {
    return fromFilter;
  }
  return trimProviderIdForApi(slot?.providerId);
}

export function resolveBookLocationId(
  selectedLocationId: string,
  slot: BookAppointmentSlot | null | undefined
): string {
  const fromFilter = String(selectedLocationId ?? '').trim();
  if (fromFilter) {
    return fromFilter;
  }
  return String(slot?.locationId ?? '').trim();
}

// `dm_email` — demographics email from login API, used for appointment confirmation toperson
const DM_EMAIL_STORAGE_KEY = 'dm_email';

// Persist `dm_email` in session only (never localStorage)
export function persistDmEmail(
  storage: { setSessionStorage(key: string, value: unknown): void },
  dmEmail: string | null | undefined
): void {
  const email = String(dmEmail ?? '').trim();
  if (!email) {
    return;
  }
  storage.setSessionStorage(DM_EMAIL_STORAGE_KEY, email);
  localStorage.removeItem(DM_EMAIL_STORAGE_KEY);
}

// Resolve toperson from session `dm_email`, then user_info.dmEmail
export function resolveConfirmationTopersonEmail(
  storage: { getSessionStorage(key: string): unknown },
  userInfo: { dmEmail?: string } | null | undefined
): string {
  const fromSession = String(storage.getSessionStorage(DM_EMAIL_STORAGE_KEY) ?? '').trim();
  return fromSession || String(userInfo?.dmEmail ?? '').trim();
}

function formatSlotTimeLabel(startTime: string): string {
  const match = startTime.match(/^(.+?)([AP]M)$/i);
  if (match) {
    const [hourMinute, period] = [match[1].trim(), match[2].toUpperCase()];
    const parts = hourMinute.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]} ${period}`;
    }
    return `${hourMinute} ${period}`;
  }
  return startTime;
}

export function resolveDefaultEnabledCalendarDate(enabledDates: Date[]): Date | null {
  if (!enabledDates.length) {
    return null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const firstOnOrAfterToday = [...enabledDates]
    .map((date) => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    })
    .filter((date) => date.getTime() >= today.getTime())
    .sort((a, b) => a.getTime() - b.getTime())[0];
  return firstOnOrAfterToday ?? enabledDates[0] ?? null;
}

// PE-6106 — only Open slots are bookable; Booked rows remain in payload for availability checks
export function isSlotStatusOpen(status: string | undefined): boolean {
  const normalized = String(status ?? '').trim();
  return !normalized || normalized === SLOT_STATUS_OPEN;
}

export function extractSlotStatusFromApiResponse(data: unknown): string {
  const rows = asRecordArray(data);
  if (!rows.length) {
    return '';
  }
  return String(rows[0]['StatusNew'] ?? '').trim();
}

export function providerHasOpenSlots(availability: GroupedDateSlots[]): boolean {
  return availability.some((dateGroup) =>
    dateGroup.slots.some((slot) => isSlotStatusOpen(slot.slotStatus))
  );
}

export function groupedSlotsHaveAvailability(groups: GroupedLocationSlots[]): boolean {
  return groups.some((locationGroup) =>
    locationGroup.providers.some((provider) => providerHasOpenSlots(provider.availability))
  );
}

function normalizeCompareValue(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function isSlotRowMissingVisitLocation(row: Record<string, unknown>): boolean {
  const rawName = row['sLocationName'];
  const rawId = row['nLocationID'];
  const nameBlank = rawName == null || String(rawName).trim() === '';
  if (!nameBlank) {
    return false;
  }
  if (rawId == null) {
    return true;
  }
  const idStr = String(rawId).trim();
  return idStr === '' || idStr === '0';
}

// PE-6127 — single eligibility gate: configured emrid/providerid match + optional user filters
function isSlotRowEligibleForDisplay(
  row: Record<string, unknown>,
  selectedLocationId: string,
  selectedLocation: LocationOption | null,
  selectedProviderId: string,
  allLocations: LocationOption[],
  allProviders: ProviderOption[]
): boolean {
  if (isSlotRowMissingVisitLocation(row)) {
    return false;
  }

  const slotLocationId = normalizeCompareValue(row['nLocationID']);
  const slotLocationName = normalizeCompareValue(row['sLocationName']);
  const slotProviderId = normalizeCompareValue(trimProviderIdForApi(row['nASBaseID']));
  if (!slotLocationId || !slotProviderId) {
    return false;
  }

  if (
    allLocations.length > 0 &&
    !allLocations.some((entry) => {
      const emrId = resolveLocationEmrId(entry);
      return emrId.length > 0 && normalizeCompareValue(emrId) === slotLocationId;
    })
  ) {
    return false;
  }

  if (
    allProviders.length > 0 &&
    !allProviders.some((entry) => {
      const listProviderId = trimProviderIdForApi(entry.provider?.['providerid']);
      return (
        listProviderId.length > 0 &&
        normalizeCompareValue(listProviderId) === slotProviderId
      );
    })
  ) {
    return false;
  }

  if (selectedProviderId && slotProviderId !== normalizeCompareValue(selectedProviderId)) {
    return false;
  }

  if (!selectedLocationId) {
    return true;
  }

  const selectedId = normalizeCompareValue(selectedLocationId);
  const selectedName = normalizeCompareValue(selectedLocation?.locationdescription);
  const selectedApiId = normalizeCompareValue(selectedLocation?.location['locationid']);
  const selectedEmrId = normalizeCompareValue(resolveLocationEmrId(selectedLocation));

  return (
    slotLocationId === selectedId ||
    (selectedEmrId.length > 0 && slotLocationId === selectedEmrId) ||
    (selectedApiId.length > 0 && slotLocationId === selectedApiId) ||
    (selectedName.length > 0 && slotLocationName === selectedName)
  );
}

function parseCalendarMonth(monthName: string): number {
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const index = months.indexOf(monthName.toLowerCase());
  if (index >= 0) {
    return index + 1;
  }
  const numeric = parseInt(monthName, 10);
  return Number.isNaN(numeric) ? 1 : numeric;
}

export function parseCalendarEnabledDates(data: unknown): Date[] {
  const rows = asRecordArray(data);
  const uniqueDatesMap = new Map<string, Date>();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const maxStart = new Date(today.getFullYear(), today.getMonth() + 6, 0);

  for (const appointment of rows) {
    // PE-6091 — JAR/PE2 rows use ENDDate; fall back to StartDate when ENDDate is blank
    const endDate = String(
      appointment['ENDDate'] ??
        appointment['endDate'] ??
        appointment['StartDate'] ??
        appointment['startDate'] ??
        ''
    ).trim();
    if (!endDate) {
      continue;
    }
    const dateParts = endDate.split('-');
    if (dateParts.length !== 3) {
      continue;
    }
    const jsDate = new Date(+dateParts[2], parseCalendarMonth(dateParts[1]) - 1, +dateParts[0]);
    if (Number.isNaN(jsDate.getTime())) {
      continue;
    }
    if (jsDate >= minStart && jsDate <= maxStart) {
      const formattedDate = `${jsDate.getFullYear()}-${jsDate.getMonth() + 1}-${jsDate.getDate()}`;
      uniqueDatesMap.set(formattedDate, jsDate);
    }
  }

  return Array.from(uniqueDatesMap.values()).sort((a, b) => a.getTime() - b.getTime());
}

function mapRowToBookAppointmentSlot(
  row: Record<string, unknown>,
  selectedDate: string
): BookAppointmentSlot | null {
  const startTime = String(row['StartTime'] ?? row['dtStartTime'] ?? row['sStartTime'] ?? '').trim();
  const masterId = String(row['nTemplateAllocationMasterID'] ?? '').trim();
  const detailId = String(row['nTemplateAllocationID'] ?? '').trim();
  if (!masterId || !detailId || !startTime) {
    return null;
  }
  const displayLabel = formatSlotTimeLabel(startTime);
  const slotDate = String(row['StartDate'] ?? selectedDate).trim();
  const nLineNumber = String(row['nLineNumber'] ?? '').trim();
  const slotStatus = String(row['StatusNew'] ?? SLOT_STATUS_OPEN).trim() || SLOT_STATUS_OPEN;
  return {
    nTemplateAllocationMasterID: masterId,
    nTemplateAllocationID: detailId,
    displayLabel,
    dtStartTime: String(row['dtStartTime'] ?? '').trim() || startTime,
    dtEndTime: String(row['dtEndTime'] ?? '').trim(),
    dtStartDate: String(row['dtStartDate'] ?? '').trim() || undefined,
    formattedDate: selectedDate,
    formattedTime: displayLabel,
    providerName: String(row['sASBaseDesc'] ?? '').trim(),
    locationName: String(row['sLocationName'] ?? '').trim(),
    providerId: trimProviderIdForApi(row['nASBaseID']),
    locationId: String(row['nLocationID'] ?? '').trim(),
    slotDate,
    nLineNumber: nLineNumber || undefined,
    slotStatus,
  };
}

// PE-6105 — stable slot identity for select/deselect toggle
export function normalizeBookDate(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

// PE-6103 — Kendo calendar bounds at midnight (current month → 6 months ahead)
export function buildBookAppointmentCalendarBounds(referenceDate: Date = new Date()): {
  minStartDate: Date;
  maxStartDate: Date;
} {
  const today = normalizeBookDate(referenceDate);
  const minStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
  const maxStartDate = new Date(today.getFullYear(), today.getMonth() + 6, 0);
  return { minStartDate, maxStartDate };
}

// PE-6324 — true when JAR enabledDates has a selectable day in that month (on/after today)
export function monthHasBookableCalendarDates(
  enabledDates: Date[],
  monthDate: Date,
  referenceDate: Date = new Date()
): boolean {
  if (!enabledDates?.length || !monthDate || Number.isNaN(monthDate.getTime())) {
    return false;
  }
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const todayMs = normalizeBookDate(referenceDate).getTime();
  return enabledDates.some((enabledDate) => {
    const normalized = normalizeBookDate(enabledDate);
    return (
      normalized.getFullYear() === year &&
      normalized.getMonth() === month &&
      normalized.getTime() >= todayMs
    );
  });
}

/**
 * PE-6324 — shrink Kendo [min]/[max] to months that actually have bookable JAR dates.
 * Year view does not honor disabledDates for month cells; out-of-range months are omitted.
 * Falls back to the full 6-month window when no bookable dates exist.
 */
export function buildBookableCalendarBounds(
  enabledDates: Date[],
  referenceDate: Date = new Date()
): { minStartDate: Date; maxStartDate: Date } {
  const fallback = buildBookAppointmentCalendarBounds(referenceDate);
  const todayMs = normalizeBookDate(referenceDate).getTime();
  const minMs = fallback.minStartDate.getTime();
  const maxMs = fallback.maxStartDate.getTime();
  const bookable = (enabledDates ?? [])
    .map(normalizeBookDate)
    .filter((date) => date.getTime() >= todayMs && date.getTime() >= minMs && date.getTime() <= maxMs)
    .sort((a, b) => a.getTime() - b.getTime());

  if (!bookable.length) {
    return fallback;
  }

  const first = bookable[0];
  const last = bookable[bookable.length - 1];
  return {
    minStartDate: new Date(first.getFullYear(), first.getMonth(), 1),
    maxStartDate: new Date(last.getFullYear(), last.getMonth() + 1, 0),
  };
}

// PE-6103 — compare calendar cells at midnight against normalized min/max
export function isBookAppointmentDateWithinCalendarBounds(
  date: Date,
  minStartDate: Date,
  maxStartDate: Date
): boolean {
  const normalizedDate = normalizeBookDate(date);
  const min = normalizeBookDate(minStartDate);
  const max = normalizeBookDate(maxStartDate);
  return normalizedDate.getTime() >= min.getTime() && normalizedDate.getTime() <= max.getTime();
}

export function formatBookAppointmentDisplayDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

export function buildBookAppointmentSlotKey(
  slot: BookAppointmentSlot,
  providerId?: string,
  locationId?: string
): string {
  const normalize = (value: unknown): string => String(value ?? '').trim();
  return [
    normalize(slot.dtStartTime),
    normalize(providerId ?? slot.providerId),
    normalize(locationId ?? slot.locationId),
    normalize(slot.nLineNumber),
    normalize(slot.nTemplateAllocationID),
    normalize(slot.nTemplateAllocationMasterID),
  ].join('\u0001');
}

// PE-6106 — keep Booked rows (Self-Scheduling-App filterSlotRowsForDisplay); UI hides non-Open slots
function filterSlotRowsForDisplay(
  data: unknown,
  selectedLocationId: string,
  selectedLocation: LocationOption | null,
  selectedProviderId: string,
  allLocations: LocationOption[],
  allProviders: ProviderOption[]
): Record<string, unknown>[] {
  return asRecordArray(data).filter((row) =>
    isSlotRowEligibleForDisplay(
      row,
      selectedLocationId,
      selectedLocation,
      selectedProviderId,
      allLocations,
      allProviders
    )
  );
}

// PE-6105 — group slots by location → provider → date (Self-Scheduling-App pattern)
export function groupSlotsFromApiResponse(
  data: unknown,
  selectedDate: string,
  selectedLocationId: string,
  selectedLocation: LocationOption | null,
  selectedProviderId: string,
  allLocations: LocationOption[] = [],
  allProviders: ProviderOption[] = []
): GroupedLocationSlots[] {
  const groups: GroupedLocationSlots[] = [];

  for (const row of filterSlotRowsForDisplay(
    data,
    selectedLocationId,
    selectedLocation,
    selectedProviderId,
    allLocations,
    allProviders
  )) {
    const slot = mapRowToBookAppointmentSlot(row, selectedDate);
    if (!slot) {
      continue;
    }

    const locationName = slot.locationName ?? '';
    let locationGroup = groups.find((entry) => entry.location === locationName);
    if (!locationGroup) {
      locationGroup = {
        location: locationName,
        locationId: slot.locationId ?? '',
        providers: [],
      };
      groups.push(locationGroup);
    }

    const providerName = slot.providerName || 'Unknown provider';
    let providerGroup = locationGroup.providers.find((entry) => entry.name === providerName);
    if (!providerGroup) {
      providerGroup = {
        name: providerName,
        id: slot.providerId ?? '',
        availability: [],
      };
      locationGroup.providers.push(providerGroup);
    }

    const slotDate = slot.slotDate || selectedDate;
    let dateGroup = providerGroup.availability.find((entry) => entry.date === slotDate);
    if (!dateGroup) {
      dateGroup = {
        date: slotDate,
        dtStartDate: String(row['dtStartDate'] ?? '').trim() || undefined,
        slots: [],
      };
      providerGroup.availability.push(dateGroup);
    }

    dateGroup.slots.push(slot);
  }

  for (const locationGroup of groups) {
    for (const providerGroup of locationGroup.providers) {
      for (const dateGroup of providerGroup.availability) {
        dateGroup.slots.sort((a, b) => Number(a.dtStartTime) - Number(b.dtStartTime));
      }
    }
  }

  return groups;
}
